import { useState } from "react";
import {
  MINIMO_DE_LETRAS_DA_SUGESTAO,
  useRegistrarConsulta,
  useSugestoesDeArtigos,
  useValorAdiado,
} from "./conhecimento";
import { podeNaSessao, useSessao } from "./sessao";

/* Autoatendimento antes do chamado.

   Conforme a pessoa digita o assunto, a tela sugere artigos (com espera de digitação, para não
   consultar a cada tecla). O que se mede é a DEFLEXÃO: quando ela lê um artigo e desiste de abrir
   o chamado, isso é registrado como `desistiu`; quando lê e abre o chamado mesmo assim, como
   `abriu_chamado`. A taxa que a equipe vê no console é desistências / (desistências + aberturas).

   Registrar é sempre "fogo e esquecimento": se a marcação falhar, a pessoa não perde nem o chamado
   nem a leitura — a métrica é da equipe, não é obstáculo para o cliente. */
export function useAjudaAntesDeAbrir(assunto: string) {
  const sessao = useSessao();
  const habilitada = podeNaSessao(sessao.data, "conhecimento:ver");
  const adiado = useValorAdiado(assunto, 350);
  const sugestoes = useSugestoesDeArtigos(adiado, habilitada);
  const registrar = useRegistrarConsulta();
  const [lidos, setLidos] = useState<string[]>([]);
  const [resolvido, setResolvido] = useState(false);

  const digitou = adiado.trim().length >= MINIMO_DE_LETRAS_DA_SUGESTAO;

  return {
    habilitada,
    /** Só há o que mostrar quando há assunto, resposta do servidor e ainda não "resolveu". */
    artigos: habilitada && digitou && !resolvido ? (sugestoes.data ?? []) : [],
    resolvido,
    lidos,
    lerArtigo(artigoId: string) {
      setLidos((atuais) => (atuais.includes(artigoId) ? atuais : [...atuais, artigoId]));
    },
    /** "Isso resolveu": a pessoa desiste de abrir o chamado depois de ler. */
    desistir(artigoId: string) {
      registrar.mutate({ artigo_id: artigoId, assunto: assunto.trim(), desfecho: "desistiu" });
      setResolvido(true);
      setLidos([]);
    },
    /** Chamado aberto depois de ler: cada artigo lido conta como "abriu mesmo assim". */
    registrarAbertura(assuntoDoChamado: string) {
      for (const artigoId of lidos) {
        registrar.mutate({
          artigo_id: artigoId,
          assunto: assuntoDoChamado.trim(),
          desfecho: "abriu_chamado",
        });
      }
      setLidos([]);
    },
    /** Voltou a digitar depois de "resolveu": recomeça. */
    recomecar() {
      setResolvido(false);
    },
  };
}

export type AjudaAntesDeAbrir = ReturnType<typeof useAjudaAntesDeAbrir>;
